import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../Login/AuthContext";
import { getRateLimitMessage } from "../../utils/rateLimitFeedback";

const RegistroUsuario = () => {
  const [form, setForm] = useState({
    username: "",
    password: "",
    email: "",
    nombre: "",
    apellido: "",
    telefono: "",
  });
  const [errores, setErrores] = useState({});
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [recaptchaSize, setRecaptchaSize] = useState("normal");
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    const actualizarTamanoCaptcha = () => {
      setRecaptchaSize(window.innerWidth < 420 ? "compact" : "normal");
    };

    actualizarTamanoCaptcha();
    window.addEventListener("resize", actualizarTamanoCaptcha);

    return () => window.removeEventListener("resize", actualizarTamanoCaptcha);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errores[name]) {
      setErrores({ ...errores, [name]: null });
    }
  };

  const validar = () => {
    const nuevosErrores = {};

    if (!form.username.trim()) {
      nuevosErrores.username = "El nombre de usuario es obligatorio.";
    } else if (form.username.trim().length < 3) {
      nuevosErrores.username = "El usuario debe tener al menos 3 caracteres.";
    } else if (form.username.length > 50) {
      nuevosErrores.username = "El usuario no puede tener más de 50 caracteres.";
    }

    if (!form.email.trim()) {
      nuevosErrores.email = "El email es obligatorio.";
    } else if (!form.email.includes("@")) {
      nuevosErrores.email = "El email no tiene un formato válido.";
    } else if (form.email.length > 120) {
      nuevosErrores.email = "El email es demasiado largo.";
    }

    if (!form.password) {
      nuevosErrores.password = "La contraseña es obligatoria.";
    } else if (form.password.length < 6) {
      nuevosErrores.password = "La contraseña debe tener al menos 6 caracteres.";
    }

    if (form.nombre && form.nombre.length > 80) {
      nuevosErrores.nombre = "El nombre no puede tener más de 80 caracteres.";
    }
    if (form.apellido && form.apellido.length > 80) {
      nuevosErrores.apellido = "El apellido no puede tener más de 80 caracteres.";
    }
    if (form.telefono && form.telefono.length > 20) {
      nuevosErrores.telefono = "El teléfono no puede tener más de 20 caracteres.";
    }

    return nuevosErrores;
  };

  const handleRegistro = async (e) => {
    e.preventDefault();

    const erroresDetectados = validar();
    if (Object.keys(erroresDetectados).length > 0) {
      setErrores(erroresDetectados);
      return;
    }

    const captchaToken = recaptchaRef.current?.getValue();
    if (!captchaToken) {
      Swal.fire({
        icon: "warning",
        title: "Verificación requerida",
        text: "Por favor completá el reCAPTCHA antes de continuar.",
        confirmButtonColor: "#dc2626",
      });
      return;
    }

    setCargando(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/registro`,
        {
          ...form,
          username: form.username.trim(),
          email: form.email.trim(),
          nombre: form.nombre.trim(),
          apellido: form.apellido.trim(),
          telefono: form.telefono.trim(),
          recaptcha_token: captchaToken,
        }
      );

      const { access_token, ...datosUsuario } = response.data;
      login(datosUsuario, access_token);

      Swal.fire({
        icon: "success",
        title: "Registro exitoso",
        text: `Bienvenida, ${datosUsuario.nombre || datosUsuario.username}`,
        showConfirmButton: false,
        timer: 1800,
      });

      navigate(location.state?.from || "/tienda", { replace: true });
    } catch (error) {
      recaptchaRef.current?.reset();
      const status = error.response?.status;
      const msg = error.response?.data?.message
        || error.response?.data?.msg
        || error.response?.data?.error
        || "";

      if (status === 409) {
        let mensaje = "Ya existe una cuenta con esos datos.";
        if (msg.toLowerCase().includes("email")) {
          mensaje = "Ese email ya está registrado. Probá iniciar sesión.";
          setErrores({ email: mensaje });
        } else if (msg.toLowerCase().includes("usuario") || msg.toLowerCase().includes("username")) {
          mensaje = "Ese nombre de usuario ya existe. Elegí otro o iniciá sesión.";
          setErrores({ username: mensaje });
        } else {
          setErrores({
            username: "Revisá este nombre de usuario.",
            email: "Revisá este correo electrónico.",
          });
        }
        Swal.fire({ icon: "info", title: "La cuenta ya existe", text: mensaje });
      } else if (status === 403) {
        Swal.fire({
          icon: "warning",
          title: "Verificación fallida",
          text: "No pudimos verificar el reCAPTCHA. Intentá de nuevo.",
          confirmButtonColor: "#dc2626",
        });
      } else if (status === 429) {
        Swal.fire({
          icon: "warning",
          title: "Demasiados intentos",
          text: getRateLimitMessage(error),
          confirmButtonColor: "#dc2626",
        });
      } else if (status === 400) {
        Swal.fire({ icon: "warning", title: "Datos inválidos", text: msg || "Revisá los campos e intentá de nuevo." });
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo registrar. Intentá de nuevo más tarde." });
      }
    } finally {
      setCargando(false);
    }
  };

  const campos = [
    { label: "Usuario", name: "username", type: "text", requerido: true },
    { label: "Email", name: "email", type: "email", requerido: true },
    { label: "Contraseña", name: "password", type: "password", requerido: true },
    { label: "Nombre", name: "nombre", type: "text", requerido: false },
    { label: "Apellido", name: "apellido", type: "text", requerido: false },
    { label: "Teléfono", name: "telefono", type: "text", requerido: false },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-6">
      <div className="w-full max-w-md p-5 sm:p-6 bg-white rounded-2xl shadow-md border border-gray-300">
        <h2 className="text-2xl font-bold mb-2 text-center text-black">
          Crear cuenta
        </h2>
        <p className="text-center text-gray-600 text-sm mb-6">
          Registrate para comprar en nuestra tienda
        </p>

        <form onSubmit={handleRegistro} className="space-y-3">
          {campos.map(({ label, name, type, requerido }) => (
            <div key={name}>
              <label className="block text-black font-medium mb-1 text-sm">
                {label} {requerido && <span className="text-gray-600">*</span>}
              </label>
              <div className={name === "password" ? "relative" : undefined}>
                <input
                  type={name === "password" && mostrarPassword ? "text" : type}
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  required={requerido}
                  className={`w-full rounded-lg border p-2 text-sm transition focus:outline-none focus:ring-2 ${
                    name === "password" ? "pr-11" : ""
                  } ${
                    errores[name]
                      ? "border-gray-400 focus:ring-gray-300 bg-white"
                      : "border-gray-300 focus:ring-gray-300"
                  }`}
                />
                {name === "password" && (
                  <button
                    type="button"
                    onClick={() => setMostrarPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 transition hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400"
                    aria-label={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    aria-pressed={mostrarPassword}
                    title={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {mostrarPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                )}
              </div>
              {errores[name] && (
                <p className="text-black text-xs mt-1 flex items-center gap-1">
                  {errores[name]}
                </p>
              )}
            </div>
          ))}

          <div className="flex justify-center pt-2">
            <div className="recaptcha-responsive">
              <ReCAPTCHA
                key={recaptchaSize}
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size={recaptchaSize}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={cargando}
            className="w-full bg-black text-white p-2 rounded-lg hover:bg-gray-900 transition font-semibold mt-2 disabled:opacity-60"
          >
            {cargando ? "Registrando..." : "Crear cuenta"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          ¿Ya tenés cuenta?{" "}
          <Link to="/login" className="text-black hover:underline font-medium">
            Iniciá sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegistroUsuario;
